// App.js
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import './App.css'; // Importar los estilos de App.css
import './apk-mode.css'; // Estilos específicos para APK Capacitor + mejoras mobile (max-w 768px)
import config from './config';

// Componentes de autenticación y suscripción
import LoginModal from './components/LoginModal';
import PricingPlans from './components/PricingPlans';
import useSubscription from './hooks/useSubscription';
import { useToastContext } from './components/Toast';

// Componentes existentes
import GenerateExam from './GenerateExam';
import Quiz from './components/Quiz';
import FlashcardStudio from './components/FlashcardStudio';
import DecisionTable from './components/DecisionTable';
import HeartFailureTable from './components/HeartFailureTable';
import HeartFailureTable1 from './components/HeartFailureTable1';
import RuidosCardiacosTable from './components/Tablaruidoscardiacos';
import EsquemaRuidosCardiacos from './components/Esquemaruidoscardiacos';
import Tabladeruidoscardiacos2 from './components/Tablaruidoscardiacos2';
import ErrorReview from './ErrorReview';
import ExamHistory from './ExamHistory';
import AdminPanel from './components/AdminPanel';
import AdminPanelSimple from './components/AdminPanelSimple';
import CompleteProfile from './components/CompleteProfile';
import AdminDashboard from './components/AdminDashboard';
import TrialBanner from './components/TrialBanner';
import TrialWidget from './components/TrialWidget';
import PausedSimulationBar from './components/PausedSimulationBar';
import CorporateFooter from './components/CorporateFooter';
import LibroReclamaciones from './components/LibroReclamaciones';
import AnimatedLogo, { LogoLoader, LogoSuccess } from './components/AnimatedLogo';
import faviconManager from './utils/faviconManager';
import AreaTrendsDashboard from './components/AreaTrendsDashboard';
import YearAnalysisDashboard from './components/YearAnalysisDashboard';
import ProgressDashboard from './components/ProgressDashboard';
import ConceptMastery from './components/ConceptMastery';
import AnalisisDashboard from './components/AnalisisMente/AnalisisDashboard';

// Toda la configuración de Supabase ahora está centralizada en el hook useSubscription

// Utilidades para trabajar con slugs consistentes entre MySQL (local) y Supabase (producción)
const normalizeText = (value = '') =>
  String(value)
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-zA-Z0-9\s-]/g, '')
    .trim()
    .toLowerCase();

const toSlug = (value = '') =>
  normalizeText(value)
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const dedupeOptions = (options = []) => {
  const seen = new Set();
  const result = [];
  for (const option of options) {
    if (!option) continue;
    const labelKey = (option.option_label || '').trim().toUpperCase();
    const fallbackKey = `${normalizeText(option.option_text || '')}|${option.id ?? ''}`;
    const key = labelKey || fallbackKey;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(option);
  }
  return result;
};

const normalizeQuestion = (question) => {
  if (!question || typeof question !== 'object') return question;
  const options = Array.isArray(question.options) ? question.options : [];
  return {
    ...question,
    options: dedupeOptions(options),
  };
};

/**
 * Mapeos "subjectId -> area" y "topicId -> topic" según tu configuración actual.
 */
const areaMap = {
  cardiologia: 'Cardiología',
  neurologia: 'Neurología'
};

const topicMap = {
  valvulopatias: 'Valvulopatías',
  miocardiopatias: 'Miocardiopatías',
  isquemiaMiocardica: 'Isquemia Miocárdica',
  fisiologiaCardiaca: 'Fisiología Cardíaca',
  ruidosCardiacos: 'Ruidos Cardíacos',
  conduccionAV: 'Conducción AV',
  cardiopatiasCongenitas: 'Cardiopatías Congénitas',
  arritmias: 'Arritmias',
  arritmiasSupraventriculares: 'Arritmias Supraventriculares',
  insuficienciaCardiaca: 'Insuficiencia Cardíaca',
  fisiologiaVascular: 'Fisiología Vascular',
  shock: 'Shock',
  electrofisiologiaCardiaca: 'Electrofisiología Cardíaca',
  emergenciasHipertensivas: 'Emergencias Hipertensivas'
};

// Iconos para cada tema (para mejorar la visualización)
const topicIcons = {
  valvulopatias: '💓',
  miocardiopatias: '🫀',
  'miocardiopatias-y-miocarditis': '🫀',
  isquemiaMiocardica: '⚡',
  'cardiopatia-isquemica': '⚡',
  fisiologiaCardiaca: '📊',
  'bases-en-cardiologia': '📊',
  ruidosCardiacos: '🔊',
  'ruidos-cardiacos': '🔊',
  conduccionAV: '⚡',
  'conduccion-av': '⚡',
  cardiopatiasCongenitas: '👶',
  'cardiopatias-congenitas': '👶',
  arritmias: '〰️',
  'arritmias-supraventriculares': '📈',
  insuficienciaCardiaca: '❤️‍🩹',
  'insuficiencia-cardiaca': '❤️‍🩹',
  fisiologiaVascular: '🩸',
  shock: '⚠️',
  'shock-enfoque-basico': '⚠️',
  electrofisiologiaCardiaca: '📝',
  emergenciasHipertensivas: '🚑',
  'hipertension-arterial': '🚑',
};

// Aliases para mapear slugs históricos (MySQL) a los utilizados actualmente en Supabase
const topicSlugAliases = {
  'miocardiopatias': 'miocardiopatias-y-miocarditis',
  'isquemia-miocardica': 'cardiopatia-isquemica',
  'fisiologia-cardiaca': 'bases-en-cardiologia',
  'ruidos-cardiacos': 'ruidos-cardiacos',
  'conduccion-av': 'conduccion-av',
  'insuficiencia-cardiaca': 'insuficiencia-cardiaca',
  'shock': 'shock-enfoque-basico',
  'emergencias-hipertensivas': 'hipertension-arterial',
};

// Iconos generales para la UI (reemplazando react-feather)
const icons = {
  heart: '❤️',
  brain: '🧠',
  bookOpen: '📖',
  clock: '⏱️',
  award: '🏆',
  history: '📜',
  logout: '🚪',
  back: '⬅️',
  menu: '☰',
  user: '👤',
  refresh: '🔄',
  warning: '⚠️',
  bookMarked: '📚',
  list: '📋',
  play: '▶️',
  pause: '⏸️',
  externalLink: '🔗',
  sun: '☀️',
  moon: '🌙',
  check: '✓',
  database: '🗄️',
  error: '❌',
};

// Menú de áreas y sus respectivos temas
const subjects = [
  {
    id: 'cardiologia',
    name: 'Cardiología',
    icon: icons.heart,
    description: "Especialidad médica que se ocupa del estudio, diagnóstico y tratamiento de las enfermedades del corazón y del sistema circulatorio.",
    topics: [
      { id: 'valvulopatias', name: 'Valvulopatías', icon: topicIcons.valvulopatias },
      { id: 'miocardiopatias', name: 'Miocardiopatías', icon: topicIcons.miocardiopatias },
      { id: 'isquemiaMiocardica', name: 'Isquemia Miocárdica', icon: topicIcons.isquemiaMiocardica },
      { id: 'fisiologiaCardiaca', name: 'Fisiología Cardíaca', icon: topicIcons.fisiologiaCardiaca },
      { id: 'ruidosCardiacos', name: 'Ruidos Cardíacos', icon: topicIcons.ruidosCardiacos },
      { id: 'conduccionAV', name: 'Conducción AV', icon: topicIcons.conduccionAV },
      { id: 'cardiopatiasCongenitas', name: 'Cardiopatías Congénitas', icon: topicIcons.cardiopatiasCongenitas },
      { id: 'arritmias', name: 'Arritmias', icon: topicIcons.arritmias },
      { id: 'arritmiasSupraventriculares', name: 'Arritmias Supraventriculares', icon: topicIcons.arritmiasSupraventriculares },
      { id: 'insuficienciaCardiaca', name: 'Insuficiencia Cardíaca', icon: topicIcons.insuficienciaCardiaca },
      { id: 'fisiologiaVascular', name: 'Fisiología Vascular', icon: topicIcons.fisiologiaVascular },
      { id: 'shock', name: 'Shock', icon: topicIcons.shock },
      { id: 'electrofisiologiaCardiaca', name: 'Electrofisiología Cardíaca', icon: topicIcons.electrofisiologiaCardiaca },
      {
        id: 'emergenciasHipertensivas',
        name: 'Emergencias Hipertensivas',
        icon: topicIcons.emergenciasHipertensivas,
        DecisionTableComponents: [DecisionTable]
      }
    ]
  },
  {
    id: 'neurologia',
    name: 'Neurología',
    icon: icons.brain,
    description: "Especialidad médica que estudia la estructura, función y desarrollo del sistema nervioso y muscular.",
    topics: [
      // Ejemplo: sin subtemas en la demo
    ]
  }
];

// CSS personalizado (inyectado al montar la aplicación)
const customStyles = `
  /* Animaciones y transiciones */
  .fade-in {
    animation: fadeIn 0.3s ease-in-out;
  }
  
  .scale-in {
    animation: scaleIn 0.2s ease-out;
  }
  
  .slide-in {
    animation: slideIn 0.3s ease-out;
  }
  
  .slide-in-right {
    animation: slideInRight 0.3s ease-out;
  }
  
  .pulse {
    animation: pulse 2s infinite;
  }
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes scaleIn {
    from { transform: scale(0.95); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
  }
  
  @keyframes slideIn {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
  
  @keyframes slideInRight {
    from { transform: translateX(20px); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
  
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }
  
  /* Transiciones */
  .btn {
    transition: all 0.2s ease;
  }
  
  .btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  }
  
  .btn:active {
    transform: translateY(0);
  }
  
  .card-hover {
    transition: all 0.3s ease;
  }
  
  .card-hover:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0,0,0,0.1);
  }
  
  /* Dark mode */
  body.dark-mode {
    background-color: #1a1a2e;
    color: #f1f1f1;
  }
  
  /* Mobile Menu */
  .mobile-menu {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    transform: translateX(-100%);
    transition: transform 0.3s ease-in-out;
  }
  
  .mobile-menu.open {
    transform: translateX(0);
  }
  
  /* Transiciones para paneles colapsables */
  .collapsible {
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.3s ease-out;
  }
  
  .collapsible.show {
    max-height: 2000px; /* valor alto para asegurar que muestre todo el contenido */
  }
  
  /* Spinner de carga */
  .spinner {
    border: 4px solid rgba(0, 0, 0, 0.1);
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border-left-color: #09f;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  /* Paginación */
  .pagination-controls {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 20px;
  }
  
  .pagination-button {
    padding: 8px 12px;
    margin: 0 4px;
    border-radius: 4px;
    border: none;
    background-color: #f0f0f0;
    cursor: pointer;
    transition: background-color 0.3s;
  }
  
  .pagination-button:hover:not(:disabled) {
    background-color: #e0e0e0;
  }
  
  .pagination-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  .pagination-button.active {
    background-color: #3498db;
    color: white;
  }
`;

function App() {
  // Debug inicial
  console.log('🚀 App.js loaded');
  console.log('  Environment:', process.env.NODE_ENV);
  console.log('  Hostname:', window.location.hostname);
  
  // Hook de notificaciones toast
  const toast = useToastContext();
  
  // Estados para modales
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showCompleteProfile, setShowCompleteProfile] = useState(false);
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);
  const [showLibroReclamaciones, setShowLibroReclamaciones] = useState(false);
  
  // Hook de suscripción
  const {
    user: authUser,
    subscription,
    loading: subscriptionLoading,
    canAnswerQuestion,
    canGenerateExam,
    canTakeSimulation,
    questionsRemaining,
    simulationsRemaining,
    trackQuestionAnswered,
    showUpgradePrompt,
    refreshSubscription,
    signOutCompletely
  } = useSubscription();
  
  // Usar el usuario del hook de suscripción
  const user = authUser;
  
  // Estado previo del usuario para detectar cambios
  const prevUserRef = useRef(user);
  
  // Estado para rastrear si ya verificamos el perfil
  const [profileChecked, setProfileChecked] = useState(false);
  
  // Detectar cambios en el usuario y mostrar notificaciones
  useEffect(() => {
    const checkUserProfile = async () => {
      if (user && !profileChecked) {
        // Marcar como verificado para no repetir
        setProfileChecked(true);
        
        // Solo verificar perfil para usuarios de Google
        if (user.app_metadata?.provider === 'google') {
          // Primero verificar localStorage como caché rápido
          const profileCompletedCache = localStorage.getItem('profile_completed_' + user.id);
          
          // Si ya está marcado como completado en localStorage, no verificar más
          if (profileCompletedCache === 'true') {
            console.log('Perfil ya completado (caché)');
          } else {
            // Si no está en caché, verificar en la base de datos
            try {
              // Usar el cliente Supabase centralizado
              const supabaseModule = await import('./supabaseClientFixed');
              const supabase = supabaseModule.default;
              
              const { data: profile, error } = await supabase
                .from('user_profiles')
                .select('id')
                .eq('user_id', user.id)
                .single();
              
              // Si existe el perfil, guardarlo en caché
              if (profile) {
                localStorage.setItem('profile_completed_' + user.id, 'true');
                console.log('Perfil encontrado en BD, guardado en caché');
              } 
              // Solo mostrar modal si NO existe el perfil
              else if (!error || error.code === 'PGRST116') {
                // PGRST116 = No rows found, que significa que no tiene perfil
                console.log('Usuario de Google sin perfil completo');
                setShowCompleteProfile(true);
                toast.info('Por favor completa tu perfil para continuar', 5000);
                return; // Salir temprano para no mostrar mensaje de bienvenida
              }
            } catch (err) {
              console.log('Error verificando perfil:', err);
              // En caso de error, asumir que el perfil existe para no molestar al usuario
            }
          }
        }
        
        // Si llega aquí, el usuario tiene perfil completo o no es de Google
        // Notificaciones removidas para evitar parpadeo
      }
    };
    
    if (prevUserRef.current !== user) {
      if (user && !prevUserRef.current) {
        // Usuario acaba de iniciar sesión
        checkUserProfile();
      } else if (!user && prevUserRef.current) {
        // Usuario acaba de cerrar sesión
        // Notificación removida para evitar parpadeo
        setShowCompleteProfile(false);
        setProfileChecked(false); // Resetear para la próxima sesión
      }
      prevUserRef.current = user;
    }
  }, [user, subscription, toast, profileChecked]);
  
  // Escuchar eventos personalizados para abrir modales
  useEffect(() => {
    const handleOpenLoginModal = () => {
      setShowLoginModal(true);
    };
    
    const handleOpenPricingModal = () => {
      setShowPricingModal(true);
    };
    
    window.addEventListener('openLoginModal', handleOpenLoginModal);
    window.addEventListener('openPricingModal', handleOpenPricingModal);
    
    return () => {
      window.removeEventListener('openLoginModal', handleOpenLoginModal);
      window.removeEventListener('openPricingModal', handleOpenPricingModal);
    };
  }, []);

  // 2) Cargar TODAS las preguntas desde el backend
  const [allQuestions, setAllQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const topicLookupBySlug = useMemo(() => {
    const lookup = {};
    allQuestions.forEach((question) => {
      if (!question || !question.topic) return;
      const slug = toSlug(question.topic);
      if (!slug) return;
      if (!lookup[slug]) {
        lookup[slug] = question.topic;
      }
    });
    return lookup;
  }, [allQuestions]);

const topicLookupKeys = useMemo(() => Object.keys(topicLookupBySlug), [topicLookupBySlug]);

  const resolveTopicSlug = useCallback(
    (rawSlug) => {
      if (!rawSlug) return '';
      let candidate = rawSlug;
      if (topicSlugAliases[candidate]) {
        candidate = topicSlugAliases[candidate];
      }
      if (topicLookupBySlug[candidate]) {
        return candidate;
      }
      const fallback = topicLookupKeys.find(
        (key) =>
          key === candidate ||
          key.startsWith(candidate) ||
          candidate.startsWith(key)
      );
      return fallback || candidate;
    },
    [topicLookupBySlug, topicLookupKeys]
  );
  
  // 3) Estados para paginación y optimización de carga
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [hasMoreQuestions, setHasMoreQuestions] = useState(true);

  // 4) Estados para subject y topic
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);

  // 5) Estado para la pantalla "Generar Examen"
  const [showGenerator, setShowGenerator] = useState(false);
  const [examQuestionsFromGenerator, setExamQuestionsFromGenerator] = useState([]);
  const [examTimeFromGenerator, setExamTimeFromGenerator] = useState(60);
  const [examModeFromGenerator, setExamModeFromGenerator] = useState("study");
  const [showQuizFromGenerator, setShowQuizFromGenerator] = useState(false);
  const [examTitleFromGenerator, setExamTitleFromGenerator] = useState("");
  const [preserveOrderFromGenerator, setPreserveOrderFromGenerator] = useState(false);
  const [simulacroIdFromGenerator, setSimulacroIdFromGenerator] = useState(null);
  const [resumeQuizId, setResumeQuizId] = useState(null);
  const [resumeDataFromHistory, setResumeDataFromHistory] = useState(null);
  const resumeFromMySQLRef = useRef(false);

  // 6) Modo simulación en el flujo actual (subject/topic)
  const [simulationMode, setSimulationMode] = useState(false);
  const [simulationTime, setSimulationTime] = useState(3);

  // 7) Estados para mostrar/ocultar tablas extra
  const [showHeartFailureTable, setShowHeartFailureTable] = useState(false);
  const [showHeartFailureTable1, setShowHeartFailureTable1] = useState(false);
  const [showRuidosCardiacosTable, setShowRuidosCardiacosTable] = useState(false);
  const [showEsquemaRuidosCardiacos, setShowEsquemaRuidosCardiacos] = useState(false);
  const [showRuidosCardiacosTable2, setShowRuidosCardiacosTable2] = useState(false);

  // 8) Estado para la Revisión de Errores
  const [showErrorReview, setShowErrorReview] = useState(false);

  // 9) Estado para mostrar el Historial de exámenes
  const [showExamHistory, setShowExamHistory] = useState(false);
  const [showFlashcardStudio, setShowFlashcardStudio] = useState(false);
  const [showTrendsDashboard, setShowTrendsDashboard] = useState(false);
  const [showYearAnalysis, setShowYearAnalysis] = useState(false);
  const [showProgressDashboard, setShowProgressDashboard] = useState(false);
  const [showConceptMastery, setShowConceptMastery] = useState(false);
  const [showAnalisisMente, setShowAnalisisMente] = useState(false);

  // 10) Nuevo: Tema oscuro/claro con persistencia en localStorage
  const [darkMode, setDarkMode] = useState(() => {
    // Recuperar el modo oscuro desde localStorage al iniciar
    const savedDarkMode = localStorage.getItem('mediexampro_darkMode');
    return savedDarkMode === 'true';
  });

  // Función para cambiar el modo oscuro sin reiniciar el quiz
  const toggleDarkMode = () => {
    setDarkMode(prevMode => {
      const newMode = !prevMode;
      // Guardar en localStorage para persistencia
      localStorage.setItem('mediexampro_darkMode', newMode.toString());
      return newMode;
    });
  };

  // 11) Nuevo: Menú móvil
  const [menuOpen, setMenuOpen] = useState(false);

  // 12) Nuevo: Efectos de animación para cards
  const [animateCards, setAnimateCards] = useState(false);

  // --- useEffects ---

  // Inyectar CSS personalizado
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.innerHTML = customStyles;
    document.head.appendChild(styleEl);
    
    // Limpieza
    return () => {
      document.head.removeChild(styleEl);
    };
  }, []);

  // Sincronización de autenticación manejada por useSubscription hook

  // Inicializar favicon manager
  useEffect(() => {
    faviconManager.init();
    
    // Easter egg: Activar modo arcoíris con Konami Code
    const konamiCode = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'];
    let konamiIndex = 0;
    
    const handleKonami = (e) => {
      if (e.key === konamiCode[konamiIndex]) {
        konamiIndex++;
        if (konamiIndex === konamiCode.length) {
          faviconManager.rainbowMode();
          toast.success('🌈 ¡Modo arcoíris activado!', 3000);
          konamiIndex = 0;
        }
      } else {
        konamiIndex = 0;
      }
    };
    
    document.addEventListener('keydown', handleKonami);
    return () => document.removeEventListener('keydown', handleKonami);
  }, [toast]);

  // Cargar preguntas desde API (optimizado para compatibilidad con la futura paginación)
  useEffect(() => {
    // URL base centralizada — auto-detecta localhost vs LAN vs producción
    const baseUrl = `${config.getApiUrl()}/questions`;
    
    console.log('Cargando preguntas desde:', baseUrl);
    setLoadingQuestions(true);
    setLoadError(null);
    
    fetch(baseUrl)
      .then((res) => {
        if (!res.ok) {
          // Si la respuesta no es exitosa (status code 200-299)
          return res.json().then(errData => {
            throw new Error(`Error ${res.status}: ${errData.error || 'Error desconocido'}`);
          });
        }
        return res.json();
      })
      .then((data) => {
        // Para versión actual (sin paginación)
        if (Array.isArray(data)) {
          console.log(`Preguntas recibidas: ${data.length}`);
          const normalizedQuestions = data.map(normalizeQuestion);
          setAllQuestions(normalizedQuestions);
          setTotalQuestions(normalizedQuestions.length);
          setHasMoreQuestions(false);
        } 
        // Para futura versión (con paginación)
        else if (data && data.data && Array.isArray(data.data)) {
          console.log(`Preguntas recibidas: ${data.data.length} de ${data.pagination?.total || 'desconocido'}`);
          const normalizedQuestions = data.data.map(normalizeQuestion);
          setAllQuestions(normalizedQuestions);
          setTotalQuestions(data.pagination?.total || normalizedQuestions.length);
          setHasMoreQuestions(data.pagination?.hasNextPage || false);
        }
        
        setLoadingQuestions(false);
        
        // Activar animación de entrada para cards
        setTimeout(() => {
          setAnimateCards(true);
        }, 100);
      })
      .catch((err) => {
        console.error('Error cargando preguntas:', err);
        setLoadError(err.message);
        setLoadingQuestions(false);
      });
  }, []);

  // Efecto para modo oscuro
  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [darkMode]);

  // Efecto para detectar APK Capacitor — añade clase 'apk-mode' al body
  // que permite estilos CSS específicos solo para el APK (cero impacto en PC).
  useEffect(() => {
    if (config.isCapacitorNative && config.isCapacitorNative()) {
      document.body.classList.add('apk-mode');
    }
  }, []);
  // NUEVO CÓDIGO: Persistencia de estado
// Reemplaza el useEffect de restauración con esta versión mejorada
useEffect(() => {
  if (loadingQuestions || loadError) return; // Esperar a que las preguntas estén cargadas
  if (resumeFromMySQLRef.current) return; // No sobreescribir si venimos de MySQL resume

  try {
    const savedState = localStorage.getItem('appState');
    if (savedState) {
      const state = JSON.parse(savedState);
      console.log('Estado guardado encontrado:', state);

      // Restaurar vista activa y parámetros del examen
      if (state.showQuizFromGenerator && state.examTitleFromGenerator) {
        console.log('Restaurando examen en progreso');
        setExamTitleFromGenerator(state.examTitleFromGenerator);
        setExamModeFromGenerator(state.examModeFromGenerator || 'study');
        setExamTimeFromGenerator(state.examTimeFromGenerator || 60);
        setPreserveOrderFromGenerator(state.preserveOrderFromGenerator || false);
        setSimulacroIdFromGenerator(state.simulacroIdFromGenerator || null);

        // Buscar el quizId y simulacroId en el estado guardado del Quiz
        const quizKeys = Object.keys(localStorage).filter(k => k.startsWith('mediexampro_quiz_state_'));
        let foundQuizState = null;
        for (const key of quizKeys) {
          try {
            const data = JSON.parse(localStorage.getItem(key));
            if (data && data.title === state.examTitleFromGenerator && !data.showResults) {
              foundQuizState = data;
              break;
            }
          } catch (_) {}
        }

        if (foundQuizState) {
          setResumeQuizId(foundQuizState.quizId);
        }

        const simId = foundQuizState?.simulacroId || state.simulacroIdFromGenerator;
        if (simId) {
          // Preguntas de simulacro: fetch desde API antes de mostrar Quiz
          const apiUrl = `${config.getApiUrl()}/simulacros/${simId}/questions`;
          fetch(apiUrl)
            .then(r => r.json())
            .then(data => {
              const formatted = data.map((q, i) => ({
                id: `sim_${q.id}`,
                year: q.year || 'Simulacro',
                especialidad: q.especialidad,
                area: q.area,
                topic: q.topic,
                subtopic: q.subtopic,
                subtopic_specific: q.subtopic_specific,
                organ_system: q.organ_system,
                case_description: q.case_description,
                question_text: q.question_text,
                explanation: q.explanation,
                key_points: q.key_points,
                ascii_diagram: q.ascii_diagram,
                options: q.options || [],
                source: 'simulacro',
                question_number: q.question_number || (i + 1),
                simulacro_source: q.simulacro_source,
                simulacro_name: q.simulacro_name
              }));
              setExamQuestionsFromGenerator(formatted);
              setShowQuizFromGenerator(true);
            })
            .catch(e => {
              console.error('Error restaurando simulacro:', e);
              // Fallback: ir al generador
              setShowGenerator(true);
            });
        } else {
          // Preguntas regulares: usar allQuestions + resumeQuizId
          setExamQuestionsFromGenerator(allQuestions);
          setShowQuizFromGenerator(true);
        }
      } else if (state.showGenerator) {
        console.log('Restaurando vista: Generator');
        setShowGenerator(true);
      } else if (state.showErrorReview) {
        console.log('Restaurando vista: Error Review');
        setShowErrorReview(true);
      } else if (state.showExamHistory) {
        console.log('Restaurando vista: Exam History');
        setShowExamHistory(true);
      } else if (state.showFlashcardStudio) {
        console.log('Restaurando vista: Flashcard Studio');
        setShowFlashcardStudio(true);
      } else if (state.showTrendsDashboard) {
        console.log('Restaurando vista: Trends Dashboard');
        setShowTrendsDashboard(true);
      } else if (state.showYearAnalysis) {
        console.log('Restaurando vista: Year Analysis');
        setShowYearAnalysis(true);
      } else if (state.showProgressDashboard) {
        console.log('Restaurando vista: Progress Dashboard');
        setShowProgressDashboard(true);
      } else if (state.showConceptMastery) {
        console.log('Restaurando vista: Concept Mastery');
        setShowConceptMastery(true);
      } else if (state.showAnalisisMente) {
        console.log('Restaurando vista: Análisis Mente Examinador');
        setShowAnalisisMente(true);
      } else {
        // Si no hay nada específico para restaurar, limpiar el estado guardado
        console.log('Mostrando página de inicio');
        localStorage.removeItem('appState');
      }
    }
  } catch (error) {
    console.error("Error al restaurar el estado:", error);
    localStorage.removeItem('appState');
  }
}, [subjects, loadingQuestions, loadError]);

// Modifica el useEffect para guardar el estado
useEffect(() => {
  // No guardar nada si estamos cargando
  if (loadingQuestions) return;
  
  // Omitimos las referencias circulares para evitar problemas al serializar
  const stateToSave = {
    selectedSubject: selectedSubject ? { id: selectedSubject.id, name: selectedSubject.name } : null,
    selectedTopic: selectedTopic ? { id: selectedTopic.id, name: selectedTopic.name } : null,
    showGenerator,
    showQuizFromGenerator,
    showErrorReview,
    showExamHistory,
    showFlashcardStudio,
    showTrendsDashboard,
    showYearAnalysis,
    showProgressDashboard,
    showConceptMastery,
    showAnalisisMente,
    examTitleFromGenerator,
    examModeFromGenerator,
    examTimeFromGenerator,
    preserveOrderFromGenerator,
    simulacroIdFromGenerator
  };

  console.log('Guardando estado de la aplicación:', stateToSave);
  localStorage.setItem('appState', JSON.stringify(stateToSave));
}, [
  loadingQuestions,
  selectedSubject,
  selectedTopic,
  showGenerator,
  showQuizFromGenerator,
  showErrorReview,
  showExamHistory,
  examTitleFromGenerator,
  examModeFromGenerator,
  examTimeFromGenerator,
  preserveOrderFromGenerator,
  simulacroIdFromGenerator,
  showFlashcardStudio,
  showTrendsDashboard,
  showYearAnalysis,
  showProgressDashboard,
  showConceptMastery,
  showAnalisisMente
]);

  // Función para manejar el intento de generar examen
  const handleGenerateExam = () => {
    // Verificar si el usuario puede generar exámenes
    const examStatus = canGenerateExam();
    
    if (!examStatus.allowed) {
      // Mostrar mensaje según el motivo
      if (examStatus.showRegister) {
        toast.warning(examStatus.reason, 5000);
        setShowLoginModal(true);
      } else if (examStatus.trialExpired) {
        toast.error(examStatus.reason, 7000);
        setShowPricingModal(true);
      } else {
        toast.warning(examStatus.reason, 5000);
      }
      return;
    }
    
    // Si está en trial, mostrar información
    if (examStatus.isTrial) {
      toast.info(`🆓 ${examStatus.reason} - Límite: ${examStatus.questionsLimit} preguntas por examen`, 5000);
    }
    
    // Permitir generar el examen
    setShowFlashcardStudio(false);
    setShowGenerator(true);
  };

  const openFlashcardStudio = () => {
    if (!user && process.env.NODE_ENV !== 'development') {
      toast.warning('Inicia sesión para usar Flashcard Studio', 4000);
      setShowLoginModal(true);
      return;
    }
    setShowFlashcardStudio(true);
    setShowGenerator(false);
    setShowQuizFromGenerator(false);
    setShowErrorReview(false);
    setShowExamHistory(false);
    setSelectedSubject(null);
    setSelectedTopic(null);
    setMenuOpen(false);
  };

  // 11) Nuevo: Breadcrumb (navegación)
  const getBreadcrumbs = () => {
    const crumbs = [];
    crumbs.push({ name: 'Inicio / Generar Examen', action: () => {
      setSelectedSubject(null);
      setSelectedTopic(null);
      handleGenerateExam(); // Verificar trial antes de generar
      setShowQuizFromGenerator(false);
      setShowErrorReview(false);
      setShowExamHistory(false);
    }});
    
    if (showGenerator) {
      // Ya no agregamos otro breadcrumb porque es la pantalla principal
    } else if (showQuizFromGenerator) {
      crumbs.push({ 
        name: 'Generar Examen', 
        action: () => {
          setShowQuizFromGenerator(false);
          setShowGenerator(true);
        } 
      });
      crumbs.push({ name: examTitleFromGenerator, action: null });
    } else if (showErrorReview) {
      crumbs.push({ name: 'Revisión de Errores', action: null });
    } else if (showExamHistory) {
      crumbs.push({ name: 'Historial de Exámenes', action: null });
    } else if (showFlashcardStudio) {
      crumbs.push({ name: 'Flashcard Studio', action: null });
    } else if (showTrendsDashboard) {
      crumbs.push({ name: 'Análisis de Tendencias', action: null });
    } else if (showProgressDashboard) {
      crumbs.push({ name: 'Mi Progreso', action: null });
    } else if (showConceptMastery) {
      crumbs.push({ name: 'Dominio por Concepto', action: null });
    } else if (showAnalisisMente) {
      crumbs.push({ name: 'Mente del Examinador', action: null });
    } else if (selectedSubject) {
      crumbs.push({ 
        name: selectedSubject.name, 
        action: () => {
          setSelectedTopic(null);
        } 
      });
      
      if (selectedTopic) {
        crumbs.push({ name: selectedTopic.name, action: null });
      }
    }
    
    return crumbs;
  };

  // --- Funciones ---

  // 1) Login manual
  const handleLogin = () => {
    setShowLoginModal(true);
  };

  // 2) Filtrar preguntas según subject y topic (flujo tradicional)
  function getFilteredQuestions(subjectId, topicId) {
    if (!subjectId || !topicId) return [];

    const area = areaMap[subjectId] || '';
    const topicName = topicMap[topicId] || '';

    const areaSlug = toSlug(area);
    const topicSlug = toSlug(topicName);
    const resolvedTopicSlug = resolveTopicSlug(topicSlug);
    const hasResolvedSlug = resolvedTopicSlug && topicLookupBySlug[resolvedTopicSlug];
    const normalizedTopicName = normalizeText(topicName);

    const filtered = allQuestions.filter((question) => {
      if (toSlug(question.area || '') !== areaSlug) return false;

      const questionSlug = toSlug(question.topic || '');

      if (hasResolvedSlug) {
        return questionSlug === resolvedTopicSlug;
      }

      if (normalizedTopicName) {
        return normalizeText(question.topic || '') === normalizedTopicName;
      }

      return false;
    });

    if (filtered.length <= 1) {
      return filtered.map(normalizeQuestion);
    }

    const uniqueQuestions = [];
    const seenKeys = new Set();
    for (const question of filtered) {
      const normalizedCase = normalizeText(question?.case_description || '');
      const normalizedQuestion = normalizeText(question?.question_text || '');
      const key = `${normalizedCase}|${normalizedQuestion}`;
      if (seenKeys.has(key)) continue;
      seenKeys.add(key);
      uniqueQuestions.push(normalizeQuestion(question));
    }

    return uniqueQuestions;
  }

  // 3) Callback para <GenerateExam />
  const handleStartExamFromGenerator = (filteredQuestions, tiempo, examMode, examTitle, preserveOrder = false, simulacroId = null) => {
    setExamQuestionsFromGenerator(filteredQuestions);
    setExamTimeFromGenerator(tiempo);
    setExamModeFromGenerator(examMode);
    setExamTitleFromGenerator(examTitle || "Examen Personalizado");
    setPreserveOrderFromGenerator(preserveOrder);
    setSimulacroIdFromGenerator(simulacroId);
    setShowGenerator(false);
    setShowQuizFromGenerator(true);
  };

  // 3b) Callback para reanudar examen desde Historial
  const handleResumeFromHistory = (resumeData) => {
    resumeFromMySQLRef.current = true; // Evita que la restauración de localStorage sobreescriba
    setExamQuestionsFromGenerator(resumeData.questions);
    setExamTitleFromGenerator(resumeData.title);
    setExamModeFromGenerator('simulation');
    setExamTimeFromGenerator(resumeData.timeAllocated ? Math.ceil(resumeData.timeAllocated / 60) : 180);
    setPreserveOrderFromGenerator(true);
    setSimulacroIdFromGenerator(resumeData.simulacroId);
    setResumeDataFromHistory(resumeData);
    setShowExamHistory(false);
    setShowQuizFromGenerator(true);
  };

  // 4) Botón "Volver al Menú de Temas" (para la parte de subject/topic)
  const handleBackToTopics = () => {
    // Resetea las tablas extra
    setShowHeartFailureTable(false);
    setShowHeartFailureTable1(false);
    setShowRuidosCardiacosTable(false);
    setShowEsquemaRuidosCardiacos(false);
    setShowRuidosCardiacosTable2(false);

    // Regresamos al selector de topics
    setSelectedTopic(null);
    setSimulationMode(false);
  };

  const handleNavigateHome = useCallback(() => {
    setShowQuizFromGenerator(false);
    setShowGenerator(false);
    setShowErrorReview(false);
    setShowExamHistory(false);
    setShowFlashcardStudio(false);
    setShowTrendsDashboard(false);
    setShowYearAnalysis(false);
    setShowProgressDashboard(false);
    setShowConceptMastery(false);
    setShowAnalisisMente(false);
    setMenuOpen(false);
    setSimulationMode(false);
    setSelectedTopic(null);
    setSelectedSubject(null);
    setShowHeartFailureTable(false);
    setShowHeartFailureTable1(false);
    setShowRuidosCardiacosTable(false);
    setShowEsquemaRuidosCardiacos(false);
    setShowRuidosCardiacosTable2(false);
    setExamQuestionsFromGenerator([]);
    setExamModeFromGenerator('study');
    setExamTimeFromGenerator(60);
    setExamTitleFromGenerator('');
    setResumeQuizId(null);
  }, []);

  // 5) Toggle del panel colapsable (para tablas complementarias)
  const togglePanel = (stateSetter, currentState) => {
    stateSetter(!currentState);
  };

  // --- Componentes Auxiliares (modificados para recibir darkMode) ---

  // Header Component
  const Header = ({ darkMode }) => (
    <header className={`py-2 px-6 ${darkMode ? 'bg-gray-900 text-white' : 'bg-white'} shadow-md fixed top-0 left-0 right-0 z-50`}>
      <div className="max-w-[1600px] mx-auto flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <button 
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
          >
            {icons.menu}
          </button>
          <AnimatedLogo 
            size="small" 
            animated={true} 
            showText={true}
            interactive={true}
            variant="full"
          />
          {/* Añadir indicador de base de datos */}
          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded ml-2">
            {totalQuestions > 0 ? `${totalQuestions} preguntas` : 'Conectado'}
          </span>
        </div>
        
        <div className="hidden md:flex items-center space-x-4">
          {user ? (
            <>
              <div className="flex items-center">
                <span className="mr-2 text-gray-600 dark:text-gray-300">{icons.user}</span>
                <span className="text-sm">
                  {process.env.NODE_ENV === 'development'
                    ? 'Alexis'
                    : user?.user_metadata?.full_name || user?.email || ''}
                </span>
              </div>
              <button
                onClick={() => setShowPricingModal(true)}
                className="btn flex items-center text-sm px-3 py-1 rounded-full bg-green-100 text-green-600 hover:bg-green-200 transition-colors mr-2"
              >
                <span className="mr-1">💎</span> {subscription?.plan === 'free' ? 'Ver Planes' : `Plan ${subscription?.plan || 'free'}`}
              </button>
              {/* Botón de Admin Dashboard (solo para admin) */}
              {user?.email === 'alexisgc90@gmail.com' && (
                <button
                  onClick={() => setShowAdminDashboard(true)}
                  className="btn flex items-center text-sm px-3 py-1 rounded-full bg-purple-100 text-purple-600 hover:bg-purple-200 transition-colors mr-2"
                >
                  <span className="mr-1">👑</span> Panel Admin
                </button>
              )}
              <button
                onClick={async () => {
                  try {
                    toast.info('Cerrando sesión...', 1000);
                    
                    // Usar la función mejorada de logout
                    const { error } = await signOutCompletely();
                    
                    if (error) {
                      console.error('Error al cerrar sesión:', error);
                      // Mantener solo el log, no mostrar toast
                      return;
                    }
                    
                    // Resetear todos los estados de la aplicación
                    setSelectedSubject(null);
                    setSelectedTopic(null);
                    setShowGenerator(false);
                    setShowErrorReview(false);
                    setShowExamHistory(false);
                    setShowQuizFromGenerator(false);
                    setShowFlashcardStudio(false);
                    
                    // Limpiar todo el localStorage de la app
                    localStorage.removeItem('appState');
                    
                    // Limpiar cualquier dato adicional de Supabase que pueda quedar
                    const keys = Object.keys(localStorage);
                    keys.forEach(key => {
                      if (key.includes('supabase') || key.includes('sb-')) {
                        localStorage.removeItem(key);
                      }
                    });
                    
                    // Notificación se mostrará automáticamente desde el useEffect
                  } catch (err) {
                    console.error('Error durante el logout:', err);
                    // Solo log, sin toast
                  }
                }}
                className="btn flex items-center text-sm px-3 py-1 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
              >
                <span className="mr-1">{icons.logout}</span> Cerrar Sesión
              </button>
            </>
          ) : (
            <button
              onClick={() => setShowLoginModal(true)}
              className="btn flex items-center text-sm px-3 py-1 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
            >
              <span className="mr-1">{icons.user}</span> Iniciar Sesión
            </button>
          )}
        </div>

        {/* Botón dark mode — fuera del 'hidden md:flex' para que se vea
            también en mobile/APK. Es el único botón visible siempre en el
            header derecho. */}
        <button
          onClick={toggleDarkMode}
          className={`btn p-2 rounded-full ml-2 flex-shrink-0 ${darkMode ? 'bg-yellow-400 text-gray-900' : 'bg-gray-800 text-white'}`}
          aria-label="Cambiar tema"
        >
          {darkMode ? icons.sun : icons.moon}
        </button>
      </div>
    </header>
  );

  // Breadcrumb Navigation
  const BreadcrumbNav = ({ darkMode }) => {
    const crumbs = getBreadcrumbs();
    
    return (
      <nav className="text-sm mb-6 fade-in">
        <ol className="flex flex-wrap items-center space-x-2">
          {crumbs.map((crumb, index) => (
            <React.Fragment key={index}>
              {index > 0 && <span className="text-gray-400">/</span>}
              <li>
                {crumb.action ? (
                  <button 
                    onClick={crumb.action}
                    className="text-blue-500 hover:text-blue-700 hover:underline"
                  >
                    {crumb.name}
                  </button>
                ) : (
                  <span className={`${darkMode ? 'text-gray-300' : 'text-gray-600'} font-medium`}>
                    {crumb.name}
                  </span>
                )}
              </li>
            </React.Fragment>
          ))}
        </ol>
      </nav>
    );
  };

  // Mobile Menu
  const MobileMenu = ({ darkMode }) => (
    <div className={`mobile-menu ${menuOpen ? 'open' : ''} ${darkMode ? 'bg-gray-900' : 'bg-white'} p-6`}>
      <div className="flex justify-between items-center mb-8">
        <span className="font-bold text-xl">MediExamPro</span>
        <button 
          onClick={() => setMenuOpen(false)}
          className="p-2"
        >
          ✕
        </button>
      </div>
      
      {user && (
        <div className={`flex items-center mb-6 p-4 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'} rounded-lg`}>
          <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold mr-3">
            {(process.env.NODE_ENV === 'development'
              ? 'A'
              : user?.user_metadata?.full_name?.[0] || 'U')}
          </div>
          <div>
            <p className="font-medium">
              {process.env.NODE_ENV === 'development'
                ? 'Alexis'
                : user?.user_metadata?.full_name || ''}
            </p>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{user?.email}</p>
          </div>
        </div>
      )}
      
      <div className="space-y-4">
        <button 
          className={`btn w-full text-left flex items-center p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}
          onClick={() => {
            setMenuOpen(false);
            setSelectedSubject(null);
            setSelectedTopic(null);
            handleGenerateExam(); // Verificar trial antes de generar
            setShowErrorReview(false);
            setShowExamHistory(false);
          }}
        >
          <span className="mr-3">{icons.bookMarked}</span> Generar Examen (Inicio)
        </button>
        
        <button 
          className={`btn w-full text-left flex items-center p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}
          onClick={() => {
            setMenuOpen(false);
            setShowErrorReview(true);
          }}
        >
          <span className="mr-3">{icons.warning}</span> Revisión de Errores
        </button>
        
        <button 
          className={`btn w-full text-left flex items-center p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}
          onClick={() => {
            setMenuOpen(false);
            setShowExamHistory(true);
          }}
        >
          <span className="mr-3">{icons.history}</span> Historial de Exámenes
        </button>
        
        <button
          className={`btn w-full text-left flex items-center p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}
          onClick={() => {
            setMenuOpen(false);
            openFlashcardStudio();
          }}
        >
          <span className="mr-3">🧠</span> Flashcard Studio
        </button>

        <button
          className={`btn w-full text-left flex items-center p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}
          onClick={() => {
            setMenuOpen(false);
            setShowTrendsDashboard(true);
          }}
        >
          <span className="mr-3">📊</span> Análisis por Área
        </button>

        <button
          className={`btn w-full text-left flex items-center p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}
          onClick={() => {
            setMenuOpen(false);
            setShowYearAnalysis(true);
          }}
        >
          <span className="mr-3">📅</span> Análisis por Año
        </button>

        <button
          className={`btn w-full text-left flex items-center p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}
          onClick={() => {
            setMenuOpen(false);
            setShowProgressDashboard(true);
          }}
        >
          <span className="mr-3">🎯</span> Mi Progreso
        </button>

        <button
          className={`btn w-full text-left flex items-center p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}
          onClick={() => {
            setMenuOpen(false);
            setShowAnalisisMente(true);
          }}
        >
          <span className="mr-3">🧠</span> Mente del Examinador
          <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-amber-400 text-amber-900 rounded-full">BETA</span>
        </button>

        <button
          onClick={toggleDarkMode}
          className={`btn w-full text-left flex items-center p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}
        >
          {darkMode ? icons.sun : icons.moon}
          <span className="ml-3">{darkMode ? 'Modo Claro' : 'Modo Oscuro'}</span>
        </button>
        
        {user && (
          <>
            <button 
              className="btn w-full text-left flex items-center p-4 rounded-lg bg-green-100 text-green-700"
              onClick={() => {
                setMenuOpen(false);
                setShowPricingModal(true);
              }}
            >
              <span className="mr-3">💎</span> Planes y Suscripciones
            </button>
            <button 
              className="btn w-full text-left flex items-center p-4 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
              onClick={async () => {
                try {
                  toast.info('Cerrando sesión...', 1000);
                  
                  // Usar la función mejorada de logout
                  const { error } = await signOutCompletely();
                  
                  if (error) {
                    console.error('Error al cerrar sesión:', error);
                    // Mantener solo el log, no mostrar toast
                    return;
                  }
                  
                  setMenuOpen(false);
                  
                  // Resetear todos los estados de la aplicación
                  setSelectedSubject(null);
                  setSelectedTopic(null);
                  setShowGenerator(false);
                  setShowErrorReview(false);
                  setShowExamHistory(false);
                  setShowQuizFromGenerator(false);
                  
                  // Limpiar todo el localStorage de la app
                  localStorage.removeItem('appState');
                  
                  // Limpiar cualquier dato adicional de Supabase que pueda quedar
                  const keys = Object.keys(localStorage);
                  keys.forEach(key => {
                    if (key.includes('supabase') || key.includes('sb-')) {
                      localStorage.removeItem(key);
                    }
                  });
                } catch (err) {
                  console.error('Error durante el logout:', err);
                  // Solo log, sin toast
                }
              }}
            >
              <span className="mr-3">{icons.logout}</span> Cerrar Sesión
            </button>
          </>
        )}
        {!user && (
          <button 
            className="btn w-full text-left flex items-center p-4 rounded-lg bg-blue-100 text-blue-700"
            onClick={() => {
              setMenuOpen(false);
              setShowLoginModal(true);
            }}
          >
            <span className="mr-3">{icons.user}</span> Iniciar Sesión
          </button>
        )}
      </div>
    </div>
  );

  // Footer Component minimalista — solo logo + nombre.
  // CorporateFooter (con RUC, contacto, libro de reclamaciones, etc.) removido
  // a pedido del usuario: app de uso personal, no comercial.
  const Footer = ({ darkMode }) => (
    <footer className={`py-4 flex items-center justify-center gap-2 ${darkMode ? 'bg-gray-900 text-gray-400' : 'bg-white text-gray-500'} border-t ${darkMode ? 'border-gray-800' : 'border-gray-200'}`}>
      <AnimatedLogo size="small" animated={false} showText={false} interactive={false} variant="full" />
      <span className="text-sm font-medium">MediExamPro</span>
    </footer>
  );

  // SubjectCard Component
  const SubjectCard = ({ subject, onSelect, darkMode }) => (
    <div
      onClick={() => onSelect(subject)}
      className={`card-hover p-6 rounded-xl cursor-pointer transition-all ${
        darkMode 
          ? 'bg-gray-800 hover:bg-gray-700 border border-gray-700' 
          : 'bg-white hover:bg-blue-50 shadow-lg hover:shadow-xl border border-gray-100'
      } ${animateCards ? 'fade-in' : ''}`}
    >
      <div className="flex items-start">
        <div className={`p-3 rounded-full mr-4 ${darkMode ? 'bg-blue-900' : 'bg-blue-100'}`}>
          <span className="text-2xl">{subject.icon}</span>
        </div>
        <div>
          <h3 className="text-xl font-bold mb-2">{subject.name}</h3>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {subject.description}
          </p>
          <p className="mt-3 text-sm">
            <span className={`font-medium ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
              {subject.topics.length}
            </span> temas disponibles
          </p>
        </div>
      </div>
    </div>
  );

  // TopicCard Component
  const TopicCard = ({ topic, onSelect, darkMode }) => (
    <div
      onClick={() => onSelect(topic)}
      className={`card-hover p-5 rounded-lg cursor-pointer transition-all flex items-center ${
        darkMode 
          ? 'bg-gray-800 hover:bg-gray-700 border border-gray-700' 
          : 'bg-white hover:bg-blue-50 shadow hover:shadow-md border border-gray-100'
      } ${animateCards ? 'slide-in' : ''}`}
    >
      <span className="text-2xl mr-3">{topic.icon}</span>
      <h3 className="text-lg font-medium">{topic.name}</h3>
    </div>
  );

  // ActionButton Component
  const ActionButton = ({ icon, text, onClick, color = "blue", className = "", darkMode }) => (
    <button
      onClick={onClick}
      className={`btn flex items-center justify-center px-4 py-3 rounded-lg text-white font-medium transition-colors ${className} ${
        color === "blue" ? "bg-blue-600 hover:bg-blue-700" :
        color === "green" ? "bg-green-600 hover:bg-green-700" :
        color === "red" ? "bg-red-600 hover:bg-red-700" :
        color === "purple" ? "bg-purple-600 hover:bg-purple-700" :
        color === "orange" ? "bg-orange-600 hover:bg-orange-700" :
        color === "gray" ? "bg-gray-600 hover:bg-gray-700" : ""
      }`}
    >
      <span className="mr-2">{icon}</span>
      <span>{text}</span>
    </button>
  );

  // Reanudar simulacro pausado desde barra global
  async function resumePausedSimulation(pausedState) {
    try {
      let state = pausedState;

      // Si viene de MySQL (PausedSimulationBar detectó sesión in_progress)
      if (state && state._source === 'mysql' && state.sessionId) {
        handleResumeFromHistory(await fetchMySQLResumeData(state.sessionId));
        toast?.success('Reanudando simulacro desde MySQL…');
        return;
      }

      if (!state) {
        const keys = Object.keys(localStorage).filter(k => k.startsWith('mediexampro_quiz_state_'));
        let latest = null;
        for (const key of keys) {
          const raw = localStorage.getItem(key);
          if (!raw) continue;
          try {
            const data = JSON.parse(raw);
            if (data && data.isPaused && !data.showResults && (data.timeLeft ?? 0) > 0) {
              if (!latest || (data.timestamp || 0) > (latest.timestamp || 0)) {
                latest = data;
              }
            }
          } catch (_) {}
        }
        state = latest;
      }

      if (!state) {
        toast?.info('No hay simulacros en pausa para reanudar');
        return;
      }

      const simMinutes = state.simulationTime || Math.max(1, Math.ceil((state.timeLeft || 60) / 60));
      const title = state.title || 'Simulacro reanudado';

      setShowGenerator(false);
      setSelectedSubject(null);
      setSelectedTopic(null);
      setShowErrorReview(false);
      setShowExamHistory(false);

      // Determinar si es un simulacro (preguntas con IDs sim_*)
      const questionIds = state.questionOrder || [];
      const isSimulacro = state.simulacroId || questionIds.some(id => String(id).startsWith('sim_'));

      let questionsToUse;
      if (isSimulacro && state.simulacroId) {
        // Fetch preguntas del simulacro desde la API
        const apiUrl = `${config.getApiUrl()}/simulacros/${state.simulacroId}/questions`;
        const response = await fetch(apiUrl);
        const data = await response.json();
        questionsToUse = data.map((q, i) => ({
          id: `sim_${q.id}`,
          year: q.year || 'Simulacro',
          especialidad: q.especialidad,
          area: q.area,
          topic: q.topic,
          subtopic: q.subtopic,
          subtopic_specific: q.subtopic_specific,
          organ_system: q.organ_system,
          case_description: q.case_description,
          question_text: q.question_text,
          explanation: q.explanation,
          key_points: q.key_points,
          ascii_diagram: q.ascii_diagram,
          options: q.options || [],
          source: 'simulacro',
          question_number: q.question_number || (i + 1),
          simulacro_source: q.simulacro_source,
          simulacro_name: q.simulacro_name
        }));
        setSimulacroIdFromGenerator(state.simulacroId);
      } else {
        if (!allQuestions || allQuestions.length === 0) {
          toast?.info('Cargando preguntas… intenta en un momento');
          return;
        }
        questionsToUse = allQuestions;
      }

      setExamQuestionsFromGenerator(questionsToUse);
      setExamTimeFromGenerator(simMinutes);
      setExamModeFromGenerator('simulation');
      setExamTitleFromGenerator(title);
      setResumeQuizId(state.quizId || null);
      setShowQuizFromGenerator(true);

      toast?.success('Reanudando simulacro…');
    } catch (e) {
      console.error('Error al reanudar simulacro:', e);
      toast?.error('Error al reanudar. Intenta de nuevo.');
    }
  }

  // Fetch datos de resume desde MySQL (usado por PausedSimulationBar y ExamHistory)
  async function fetchMySQLResumeData(sessionId) {
    const apiUrl = config.getApiUrl();
    const res = await fetch(`${apiUrl}/exam-sessions/${sessionId}/resume`);
    const data = await res.json();
    if (!data.questions || data.questions.length === 0) {
      throw new Error('No se pudieron cargar las preguntas');
    }
    const { session, answers, questions } = data;

    const userAnswers = questions.map((q, i) => {
      const answer = answers[i];
      if (!answer || answer.status === 'skipped' || !answer.user_answer_label) return null;
      const matchedOpt = (q.options || []).find(
        opt => opt.option_label && opt.option_label.toLowerCase() === answer.user_answer_label.toLowerCase()
      );
      return matchedOpt ? matchedOpt.id : null;
    });

    const questionNotes = {};
    answers.forEach((a, i) => {
      if (a.note || a.confidence != null) {
        questionNotes[i] = { text: a.note || '', confidence: a.confidence ?? null };
      }
    });

    const markedForReview = {};
    answers.forEach((a, i) => { if (a.flagged) markedForReview[i] = true; });

    const questionTimes = {};
    answers.forEach((a, i) => { if (a.time_spent_seconds) questionTimes[i] = a.time_spent_seconds; });

    const correctCount = answers.filter(a => a.status === 'correct').length;
    const incorrectCount = answers.filter(a => a.status === 'incorrect').length;

    return {
      questions,
      title: session.exam_title || 'Examen sin Título',
      simulacroId: session.simulacro_id || null,
      sessionId: session.id,
      currentQuestion: session.current_question || 0,
      timeAllocated: session.time_allocated_seconds || null,
      timeSpent: session.time_spent_seconds || 0,
      userAnswers,
      questionNotes,
      markedForReview,
      questionTimes,
      correctAnswersCount: correctCount,
      incorrectAnswersCount: incorrectCount,
    };
  }

  // PageLayout component
  const PageLayout = ({ children, darkMode, wide = false }) => (
    <div className={`min-h-screen flex flex-col ${darkMode ? 'bg-gray-800 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* TrialBanner removido — single user, plan pro permanente. */}

      <Header darkMode={darkMode} />
      <MobileMenu darkMode={darkMode} />

      <main className={`flex-grow pt-24 pb-8 ${wide ? 'px-0' : 'px-4'}`}>
        <div className={wide ? 'w-full' : 'max-w-[1600px] mx-auto'}>
          {!wide && <BreadcrumbNav darkMode={darkMode} />}
          <div className="fade-in">
            {children}
          </div>
        </div>
      </main>
      
      <Footer darkMode={darkMode} />

      {/* Botón flotante de Libro de Reclamaciones removido según solicitud */}
      {!showQuizFromGenerator && !selectedTopic && <PausedSimulationBar darkMode={darkMode} onResume={resumePausedSimulation} />}
    </div>
  );

  // --- Render condicional principal ---
  const renderMainContent = () => {
    // Ya no bloqueamos la app si no hay usuario - login es opcional
    // Solo mostramos un banner sugeriendo login
    if (false && !user && process.env.NODE_ENV !== 'development') {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-100'}`}>
        <div className={`p-10 rounded-xl shadow-2xl text-center max-w-md w-full scale-in ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="mb-8">
            <div className="text-5xl mx-auto text-red-500 mb-4">{icons.heart}</div>
            <h1 className="text-3xl font-bold mb-2">MediExamPro</h1>
            <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Plataforma de estudio para profesionales médicos</p>
          </div>
          
          <h2 className="text-xl font-semibold mb-6">Inicia sesión para continuar</h2>
          
          <button
            onClick={handleLogin}
            className="btn w-full py-3 px-6 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition flex items-center justify-center"
          >
            <span className="mr-2">{icons.user}</span> Ingresar
          </button>
          
          <p className={`mt-6 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Sistema de evaluación y estudio para medicina.
          </p>
        </div>
        
        {/* Tema oscuro/claro */}
        <button
          onClick={toggleDarkMode}
          className={`btn mt-6 p-3 rounded-full ${darkMode ? 'bg-yellow-400 text-gray-900' : 'bg-gray-800 text-white'}`}
          aria-label="Cambiar tema"
        >
          {darkMode ? icons.sun : icons.moon}
        </button>
      </div>
    );
  }

  // (b) Cargando preguntas
  if (loadingQuestions) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-100'}`}>
        <LogoLoader message="Cargando preguntas..." />
      </div>
    );
  }

  // (b.2) Error de carga
  if (loadError) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-100'}`}>
        <div className={`text-center max-w-md p-8 ${darkMode ? 'bg-red-900' : 'bg-red-50'} rounded-xl`}>
          <div className="mx-auto mb-4">
            <span className="text-5xl text-red-500">{icons.error}</span>
          </div>
          <p className={`text-xl font-medium ${darkMode ? 'text-red-300' : 'text-red-700'}`}>Error al cargar preguntas</p>
          <p className={`${darkMode ? 'text-gray-200' : 'text-gray-700'} mt-2 mb-4`}>{loadError}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  // Vista de Análisis de Tendencias por Área
  if (showTrendsDashboard) {
    return (
      <PageLayout darkMode={darkMode}>
        <AreaTrendsDashboard
          questions={allQuestions}
          darkMode={darkMode}
          onBack={() => setShowTrendsDashboard(false)}
        />
      </PageLayout>
    );
  }

  // Vista de Análisis por Año
  if (showYearAnalysis) {
    return (
      <PageLayout darkMode={darkMode}>
        <YearAnalysisDashboard
          questions={allQuestions}
          darkMode={darkMode}
          onBack={() => setShowYearAnalysis(false)}
        />
      </PageLayout>
    );
  }

  // Vista de Mi Progreso
  if (showProgressDashboard) {
    return (
      <PageLayout darkMode={darkMode}>
        <ProgressDashboard
          darkMode={darkMode}
          onBack={() => setShowProgressDashboard(false)}
        />
      </PageLayout>
    );
  }

  // Vista Mente del Examinador (BETA)
  if (showAnalisisMente) {
    return (
      <PageLayout darkMode={darkMode} wide>
        <AnalisisDashboard
          darkMode={darkMode}
          onBack={() => setShowAnalisisMente(false)}
        />
      </PageLayout>
    );
  }

  // Vista Dominio por Concepto
  if (showConceptMastery) {
    return (
      <PageLayout darkMode={darkMode}>
        <div style={{ marginBottom: 16 }}>
          <button
            onClick={() => setShowConceptMastery(false)}
            className={`px-4 py-2 rounded-lg font-semibold ${darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
          >
            ← Volver al inicio
          </button>
        </div>
        <ConceptMastery darkMode={darkMode} />
      </PageLayout>
    );
  }

  // (A) Vista Historial de exámenes
  if (showExamHistory) {
    return (
      <PageLayout darkMode={darkMode}>
        <div className={`p-8 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-lg'}`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="mr-3 text-blue-500">{icons.history}</span> Historial de Exámenes
          </h2>
          <ExamHistory onBack={() => setShowExamHistory(false)} darkMode={darkMode} onResumeExam={handleResumeFromHistory} />
        </div>
      </PageLayout>
    );
  }

  if (showFlashcardStudio) {
    return (
      <PageLayout darkMode={darkMode}>
        <div className={`p-4 sm:p-6 rounded-3xl ${darkMode ? 'bg-gray-900' : 'bg-white shadow-2xl'}`}>
          <FlashcardStudio
            darkMode={darkMode}
            onBack={() => setShowFlashcardStudio(false)}
            user={user}
          />
        </div>
      </PageLayout>
    );
  }

  // Si se activa la vista de Revisión de Errores, mostramos <ErrorReview />
  if (showErrorReview) {
    return (
      <PageLayout darkMode={darkMode}>
        <div className={`p-8 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-lg'}`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="mr-3 text-orange-500">{icons.warning}</span> Revisión de Errores
          </h2>
          <ErrorReview
            allQuestions={allQuestions}
            onBack={() => setShowErrorReview(false)}
            darkMode={darkMode}
          />
        </div>
      </PageLayout>
    );
  }

  // (c) Si showQuizFromGenerator está activo -> <Quiz> con preguntas generadas
  if (showQuizFromGenerator) {
    return (
      <PageLayout darkMode={darkMode}>
        <div className={`p-8 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-lg'}`}>
          <Quiz
            title={examTitleFromGenerator || "Examen Personalizado"}
            questions={examQuestionsFromGenerator}
            simulationMode={examModeFromGenerator === "simulation"}
            simulationTime={examModeFromGenerator === "simulation" ? examTimeFromGenerator : null}
            preserveOrder={preserveOrderFromGenerator}
            resumeQuizId={resumeQuizId}
            simulacroId={simulacroIdFromGenerator}
            resumeFromMySQL={resumeDataFromHistory}
            onBack={() => {
              setShowQuizFromGenerator(false);
              setShowGenerator(true);
              setResumeQuizId(null);
              setResumeDataFromHistory(null);
            }}
            onGoHome={handleNavigateHome}
            fromGenerator={true}
            darkMode={darkMode}
          />
        </div>
      </PageLayout>
    );
  }

  // (d) Si showGenerator está activo -> mostrar <GenerateExam />
  if (showGenerator) {
    return (
      <PageLayout darkMode={darkMode}>
        <div className={`p-8 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-lg'}`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="mr-3 text-green-500">{icons.bookMarked}</span> Generador de Exámenes
          </h2>
          <GenerateExam
            allQuestions={allQuestions}
            onStartExam={handleStartExamFromGenerator}
            onBack={() => setShowGenerator(false)}
            onGoHome={handleNavigateHome}
            darkMode={darkMode}
          />
        </div>
      </PageLayout>
    );
  }

  // (e) Por defecto -> Mostrar página de inicio mejorada
  // Siempre mostrar la página de inicio cuando no hay ninguna vista específica activa
  if (!selectedSubject && !showGenerator && !showErrorReview && !showExamHistory && !showQuizFromGenerator && !showFlashcardStudio && !showTrendsDashboard && !showYearAnalysis && !showProgressDashboard && !showConceptMastery && !showAnalisisMente) {
    return (
      <PageLayout darkMode={darkMode}>
        <div className="space-y-8">
          {/* Hero Section Premium */}
          <div className={`relative overflow-hidden p-12 rounded-3xl ${
            darkMode 
              ? 'bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900' 
              : 'bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600'
          } shadow-2xl`}>
            {/* Efectos de fondo animados */}
            <div className="absolute inset-0">
              <div className="absolute top-0 -left-4 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
              <div className="absolute top-0 -right-4 w-72 h-72 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
              <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-4000"></div>
            </div>
            
            <div className="relative z-10 text-center">
              <div className="inline-flex items-center justify-center mb-6">
                <AnimatedLogo 
                  size="hero" 
                  animated={true} 
                  showText={false}
                  interactive={true}
                  variant="full"
                />
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 animate-fade-in">
                {user ? `¡Hola, ${user.user_metadata?.full_name || user.email?.split('@')[0]}!` : 'MediExamPro'}
              </h1>
              <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-2xl mx-auto">
                Tu camino hacia el éxito en el Residentado Médico comienza aquí
              </p>
              
              {/* Indicador de suscripción */}
              <div className="inline-flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-white mb-6">
                <span className="mr-2">✨</span>
                <span className="font-medium">
                  {subscription?.isAdmin ? 'Modo Admin' : 
                   subscription?.plan === 'pro' ? 'Plan PRO' : 
                   user ? 'Plan Gratuito (15 preguntas)' : 'Modo Invitado (5 preguntas)'}
                </span>
              </div>
              
              {/* Botones de Acción Rápida */}
              <div className="flex flex-wrap justify-center gap-4 mt-8">
                <button
                  onClick={handleGenerateExam}
                  className="px-8 py-4 bg-white/20 backdrop-blur-sm border-2 border-white/50 rounded-full text-white font-bold hover:bg-white/30 transition-all transform hover:scale-105 hover:shadow-xl flex items-center gap-2"
                >
                  <span className="text-2xl">✏️</span>
                  <span>Generar Examen</span>
                </button>
                
                <button
                  onClick={() => setShowErrorReview(true)}
                  className="px-8 py-4 bg-white/20 backdrop-blur-sm border-2 border-white/50 rounded-full text-white font-bold hover:bg-white/30 transition-all transform hover:scale-105 hover:shadow-xl flex items-center gap-2"
                >
                  <span className="text-2xl">🔍</span>
                  <span>Revisar Errores</span>
                </button>
                
                <button
                  onClick={() => setShowExamHistory(true)}
                  className="px-8 py-4 bg-white/20 backdrop-blur-sm border-2 border-white/50 rounded-full text-white font-bold hover:bg-white/30 transition-all transform hover:scale-105 hover:shadow-xl flex items-center gap-2"
                >
                  <span className="text-2xl">📈</span>
                  <span>Mi Historial</span>
                </button>
                
                <button
                  onClick={openFlashcardStudio}
                  className="px-8 py-4 bg-white/20 backdrop-blur-sm border-2 border-white/50 rounded-full text-white font-bold hover:bg-white/30 transition-all transform hover:scale-105 hover:shadow-xl flex items-center gap-2"
                >
                  <span className="text-2xl">🧠</span>
                  <span>Flashcards Studio</span>
                </button>

                <button
                  onClick={() => setShowTrendsDashboard(true)}
                  className="px-8 py-4 bg-gradient-to-r from-teal-500/80 to-cyan-500/80 backdrop-blur-sm border-2 border-teal-300/50 rounded-full text-white font-bold hover:from-teal-500 hover:to-cyan-500 transition-all transform hover:scale-105 hover:shadow-xl flex items-center gap-2"
                >
                  <span className="text-2xl">📊</span>
                  <span>Análisis de Tendencias</span>
                </button>

                <button
                  onClick={() => setShowProgressDashboard(true)}
                  className="px-8 py-4 bg-gradient-to-r from-emerald-500/80 to-teal-500/80 backdrop-blur-sm border-2 border-emerald-300/50 rounded-full text-white font-bold hover:from-emerald-500 hover:to-teal-500 transition-all transform hover:scale-105 hover:shadow-xl flex items-center gap-2"
                >
                  <span className="text-2xl">🎯</span>
                  <span>Mi Progreso</span>
                </button>

                <button
                  onClick={() => setShowAnalisisMente(true)}
                  className="px-8 py-4 bg-gradient-to-r from-fuchsia-500/80 to-purple-600/80 backdrop-blur-sm border-2 border-fuchsia-300/50 rounded-full text-white font-bold hover:from-fuchsia-500 hover:to-purple-600 transition-all transform hover:scale-105 hover:shadow-xl flex items-center gap-2 relative"
                >
                  <span className="text-2xl">🧠</span>
                  <span>Mente del Examinador</span>
                  <span className="absolute -top-1.5 -right-1.5 px-1.5 py-0.5 text-[10px] font-bold bg-amber-400 text-amber-900 rounded-full shadow">BETA</span>
                </button>

              </div>
            </div>
          </div>

          {/* Estadística Principal Centrada */}
          <div className="flex justify-center mb-8">
            <div className={`group p-8 rounded-2xl transition-all transform hover:scale-105 hover:shadow-2xl ${
              darkMode 
                ? 'bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900' 
                : 'bg-gradient-to-br from-indigo-400 via-purple-500 to-pink-500'
            } max-w-md w-full`}>
              <div className="text-center text-white">
                <div className="text-6xl mb-4 animate-pulse">🎓</div>
                <p className="text-5xl font-bold mb-2 animate-fade-in">{allQuestions.length.toLocaleString()}</p>
                <p className="text-xl font-medium">Preguntas Disponibles de</p>
                <p className="text-xl font-medium">Especialidades Médicas</p>
                <div className="mt-4 inline-flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                  <span className="text-sm">Actualizado diariamente</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Funciones Principales con Nuevo Diseño */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div 
              onClick={handleGenerateExam}
              className={`group cursor-pointer relative overflow-hidden rounded-2xl transition-all transform hover:scale-105 ${
                darkMode 
                  ? 'bg-gradient-to-br from-cyan-900 to-blue-900 hover:from-cyan-800 hover:to-blue-800' 
                  : 'bg-gradient-to-br from-cyan-400 to-blue-500 hover:from-cyan-500 hover:to-blue-600'
              } p-8 shadow-xl hover:shadow-2xl`}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12"></div>
              <div className="relative z-10 text-white text-center">
                <div className="text-5xl mb-4 transform group-hover:rotate-12 transition-transform">✏️</div>
                <h3 className="text-2xl font-bold mb-2">Generar Examen</h3>
                <p className="text-white/90">Crea evaluaciones personalizadas por especialidad y tema</p>
                <div className="mt-4 inline-flex items-center text-sm bg-white/20 px-3 py-1 rounded-full">
                  <span>Iniciar →</span>
                </div>
              </div>
            </div>

            <div 
              onClick={() => setShowErrorReview(true)}
              className={`group cursor-pointer relative overflow-hidden rounded-2xl transition-all transform hover:scale-105 ${
                darkMode 
                  ? 'bg-gradient-to-br from-orange-900 to-red-900 hover:from-orange-800 hover:to-red-800' 
                  : 'bg-gradient-to-br from-orange-400 to-red-500 hover:from-orange-500 hover:to-red-600'
              } p-8 shadow-xl hover:shadow-2xl`}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12"></div>
              <div className="relative z-10 text-white text-center">
                <div className="text-5xl mb-4 transform group-hover:rotate-12 transition-transform">🔍</div>
                <h3 className="text-2xl font-bold mb-2">Revisión de Errores</h3>
                <p className="text-white/90">Analiza y aprende de tus respuestas incorrectas</p>
                <div className="mt-4 inline-flex items-center text-sm bg-white/20 px-3 py-1 rounded-full">
                  <span>Revisar →</span>
                </div>
              </div>
            </div>

            <div 
              onClick={() => setShowExamHistory(true)}
              className={`group cursor-pointer relative overflow-hidden rounded-2xl transition-all transform hover:scale-105 ${
                darkMode 
                  ? 'bg-gradient-to-br from-purple-900 to-pink-900 hover:from-purple-800 hover:to-pink-800' 
                  : 'bg-gradient-to-br from-purple-400 to-pink-500 hover:from-purple-500 hover:to-pink-600'
              } p-8 shadow-xl hover:shadow-2xl`}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12"></div>
              <div className="relative z-10 text-white text-center">
                <div className="text-5xl mb-4 transform group-hover:rotate-12 transition-transform">📈</div>
                <h3 className="text-2xl font-bold mb-2">Mi Historial</h3>
                <p className="text-white/90">Visualiza tu progreso y estadísticas detalladas</p>
                <div className="mt-4 inline-flex items-center text-sm bg-white/20 px-3 py-1 rounded-full">
                  <span>Ver más →</span>
                </div>
              </div>
            </div>

            <div
              onClick={openFlashcardStudio}
              className={`group cursor-pointer relative overflow-hidden rounded-2xl transition-all transform hover:scale-105 ${
                darkMode
                  ? 'bg-gradient-to-br from-emerald-900 to-cyan-900 hover:from-emerald-800 hover:to-cyan-800'
                  : 'bg-gradient-to-br from-emerald-400 to-cyan-500 hover:from-emerald-500 hover:to-cyan-600'
              } p-8 shadow-xl hover:shadow-2xl`}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12"></div>
              <div className="relative z-10 text-white text-center">
                <div className="text-5xl mb-4 transform group-hover:rotate-12 transition-transform">🧠</div>
                <h3 className="text-2xl font-bold mb-2">Flashcard Studio</h3>
                <p className="text-white/90">Importa decks Anki, crea mnemotecnias y exporta en segundos</p>
                <div className="mt-4 inline-flex items-center text-sm bg-white/20 px-3 py-1 rounded-full">
                  <span>Crear deck →</span>
                </div>
              </div>
            </div>
          </div>

          {/* Análisis por Área - Tarjeta destacada */}
          <div
            onClick={() => setShowTrendsDashboard(true)}
            className={`group cursor-pointer relative overflow-hidden rounded-2xl transition-all transform hover:scale-[1.02] ${
              darkMode
                ? 'bg-gradient-to-br from-teal-900 via-cyan-900 to-blue-900 hover:from-teal-800 hover:via-cyan-800 hover:to-blue-800'
                : 'bg-gradient-to-br from-teal-400 via-cyan-500 to-blue-500 hover:from-teal-500 hover:via-cyan-600 hover:to-blue-600'
            } p-8 shadow-xl hover:shadow-2xl mb-6`}
          >
            <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full -mr-24 -mt-24"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full -ml-16 -mb-16"></div>
            <div className="absolute top-1/2 right-8 transform -translate-y-1/2">
              <span className="text-8xl opacity-20">📊</span>
            </div>
            <div className="relative z-10 text-white">
              <div className="flex items-center gap-4 mb-4">
                <div className="text-5xl transform group-hover:rotate-12 transition-transform">📊</div>
                <div>
                  <h3 className="text-2xl font-bold">Análisis por Área</h3>
                  <p className="text-white/80 text-sm">Predice qué temas caerán en tu examen</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold">🔥</p>
                  <p className="text-xs text-white/80">Heatmaps</p>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold">📈</p>
                  <p className="text-xs text-white/80">Tendencias</p>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold">🎯</p>
                  <p className="text-xs text-white/80">Predicción</p>
                </div>
              </div>
              <p className="text-white/90 mb-4">Visualiza patrones históricos por área, identifica temas recurrentes y enfoca tu estudio en lo más probable</p>
              <div className="inline-flex items-center text-sm bg-white/20 px-4 py-2 rounded-full">
                <span>Explorar por área →</span>
              </div>
            </div>
          </div>

          {/* Análisis por Año - Tarjeta destacada NUEVA */}
          <div
            onClick={() => setShowYearAnalysis(true)}
            className={`group cursor-pointer relative overflow-hidden rounded-2xl transition-all transform hover:scale-[1.02] ${
              darkMode
                ? 'bg-gradient-to-br from-violet-900 via-purple-900 to-indigo-900 hover:from-violet-800 hover:via-purple-800 hover:to-indigo-800'
                : 'bg-gradient-to-br from-violet-400 via-purple-500 to-indigo-500 hover:from-violet-500 hover:via-purple-600 hover:to-indigo-600'
            } p-8 shadow-xl hover:shadow-2xl mb-6`}
          >
            <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full -mr-24 -mt-24"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full -ml-16 -mb-16"></div>
            <div className="absolute top-1/2 right-8 transform -translate-y-1/2">
              <span className="text-8xl opacity-20">📅</span>
            </div>
            <div className="relative z-10 text-white">
              <div className="flex items-center gap-4 mb-4">
                <div className="text-5xl transform group-hover:rotate-12 transition-transform">📅</div>
                <div>
                  <h3 className="text-2xl font-bold">Análisis por Año</h3>
                  <p className="text-white/80 text-sm">Explora la distribución de cada examen</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold">📊</p>
                  <p className="text-xs text-white/80">Distribución</p>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold">🏆</p>
                  <p className="text-xs text-white/80">Top Temas</p>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold">⚖️</p>
                  <p className="text-xs text-white/80">Comparar</p>
                </div>
              </div>
              <p className="text-white/90 mb-4">Analiza cómo se distribuyeron las áreas y temas en cada año, compara con el promedio histórico</p>
              <div className="inline-flex items-center text-sm bg-white/20 px-4 py-2 rounded-full">
                <span>Explorar por año →</span>
              </div>
            </div>
          </div>

          {/* Mi Progreso - Tarjeta destacada */}
          <div
            onClick={() => setShowProgressDashboard(true)}
            className={`group cursor-pointer relative overflow-hidden rounded-2xl transition-all transform hover:scale-[1.02] ${
              darkMode
                ? 'bg-gradient-to-br from-emerald-900 via-teal-900 to-cyan-900 hover:from-emerald-800 hover:via-teal-800 hover:to-cyan-800'
                : 'bg-gradient-to-br from-emerald-400 via-teal-500 to-cyan-500 hover:from-emerald-500 hover:via-teal-600 hover:to-cyan-600'
            } p-8 shadow-xl hover:shadow-2xl mb-6`}
          >
            <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full -mr-24 -mt-24"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full -ml-16 -mb-16"></div>
            <div className="relative z-10 text-white">
              <div className="flex items-center gap-4 mb-4">
                <div className="text-5xl transform group-hover:rotate-12 transition-transform">🎯</div>
                <div>
                  <h3 className="text-2xl font-bold">Mi Progreso</h3>
                  <p className="text-white/80 text-sm">Visualiza tu avance por area y tema</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold">📊</p>
                  <p className="text-xs text-white/80">Accuracy</p>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold">📈</p>
                  <p className="text-xs text-white/80">Tendencias</p>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold">🔴</p>
                  <p className="text-xs text-white/80">Debilidades</p>
                </div>
              </div>
              <p className="text-white/90 mb-4">Ve tu rendimiento por area, descubre temas debiles y sigue tu mejora en el tiempo</p>
              <div className="inline-flex items-center text-sm bg-white/20 px-4 py-2 rounded-full">
                <span>Ver mi progreso →</span>
              </div>
            </div>
          </div>

          {/* Dominio por Concepto - Tarjeta */}
          <div
            onClick={() => setShowConceptMastery(true)}
            className={`group cursor-pointer relative overflow-hidden rounded-2xl transition-all transform hover:scale-[1.02] ${
              darkMode
                ? 'bg-gradient-to-br from-violet-900 via-purple-900 to-indigo-900 hover:from-violet-800 hover:via-purple-800 hover:to-indigo-800'
                : 'bg-gradient-to-br from-violet-400 via-purple-500 to-indigo-500 hover:from-violet-500 hover:via-purple-600 hover:to-indigo-600'
            } p-8 shadow-xl hover:shadow-2xl mb-6`}
          >
            <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full -mr-24 -mt-24"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full -ml-16 -mb-16"></div>
            <div className="relative z-10 text-white">
              <div className="flex items-center gap-4 mb-4">
                <div className="text-5xl transform group-hover:rotate-12 transition-transform">🧠</div>
                <div>
                  <h3 className="text-2xl font-bold">Dominio por Concepto</h3>
                  <p className="text-white/80 text-sm">Mide tu nivel real por tema con rondas de verificación</p>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center">
                  <p className="text-lg font-bold">✓</p>
                  <p className="text-xs text-white/80">Sólidos</p>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center">
                  <p className="text-lg font-bold">↑</p>
                  <p className="text-xs text-white/80">Mejorando</p>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center">
                  <p className="text-lg font-bold">↓</p>
                  <p className="text-xs text-white/80">Débiles</p>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center">
                  <p className="text-lg font-bold">○</p>
                  <p className="text-xs text-white/80">Pendientes</p>
                </div>
              </div>
              <p className="text-white/90 mb-4">Baseline RM 2025 + frecuencia histórica + rondas de verificación = dominio medido</p>
              <div className="inline-flex items-center text-sm bg-white/20 px-4 py-2 rounded-full">
                <span>Ver dominio por concepto →</span>
              </div>
            </div>
          </div>

          {/* Características del Sistema */}
          <div className={`p-8 rounded-2xl ${darkMode ? 'bg-gray-800/50 backdrop-blur' : 'bg-white/80 backdrop-blur shadow-lg'}`}>
            <h2 className="text-2xl font-bold text-center mb-8">
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                ¿Por qué elegir MediExamPro?
              </span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-4xl mb-3">🎯</div>
                <h3 className={`font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Preguntas Actualizadas</h3>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Base de datos en constante actualización con preguntas de residentado médico
                </p>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-3">📊</div>
                <h3 className={`font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Análisis Detallado</h3>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Estadísticas completas de tu rendimiento por especialidad y tema
                </p>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-3">🚀</div>
                <h3 className={`font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Modo Simulacro</h3>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Practica con temporizador como en el examen real
                </p>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-3">🧠</div>
                <h3 className={`font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Flashcards Pro</h3>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Convierte errores en decks visuales, importa Anki y exporta tus repasos clínicos.
                </p>
              </div>
            </div>
          </div>
          
          {/* Call to Action para usuarios no registrados */}
          {!user && (
            <div className={`relative overflow-hidden p-8 rounded-xl ${darkMode ? 'bg-gradient-to-r from-purple-900 to-pink-900' : 'bg-gradient-to-r from-purple-500 to-pink-500'} shadow-xl`}>
              <div className="text-center text-white">
                <h3 className="text-2xl font-bold mb-3">🚀 Desbloquea Todo el Potencial</h3>
                <p className="mb-6 text-white/90">Regístrate gratis y accede a 15 preguntas por sesión, o actualiza a PRO para preguntas ilimitadas</p>
                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => setShowLoginModal(true)}
                    className="px-6 py-3 bg-white text-purple-600 font-bold rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    Iniciar Sesión
                  </button>
                  <button
                    onClick={() => setShowPricingModal(true)}
                    className="px-6 py-3 bg-purple-700 text-white font-bold rounded-lg hover:bg-purple-800 transition-colors border-2 border-white/30"
                  >
                    Ver Planes PRO
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </PageLayout>
    );
  }

  // (f) Si no hay selectedTopic -> mostrar menú de temas
  if (!selectedTopic) {
    return (
      <PageLayout darkMode={darkMode}>
        <div className={`p-8 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-lg'}`}>
          <div className="flex items-center mb-6">
            <span className="text-3xl mr-3">{selectedSubject.icon}</span>
            <h2 className="text-2xl font-bold">{selectedSubject.name}</h2>
          </div>
          
          {/* Controles para modo simulacro */}
          <div className={`mb-8 p-5 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
            <h3 className="text-lg font-medium mb-4 flex items-center">
              <span className="mr-2 text-blue-500">{icons.clock}</span> Configuración de Simulacro
            </h3>
            
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center">
                <label className="mr-3 font-medium">Duración:</label>
                <input
                  type="number"
                  value={simulationTime}
                  onChange={(e) => setSimulationTime(Number(e.target.value))}
                  className={`w-20 p-2 rounded text-center ${
                    darkMode 
                      ? 'bg-gray-800 border-gray-600' 
                      : 'border border-gray-300'
                  }`}
                  min="1"
                /> 
                <span className="ml-2">min</span>
              </div>
              
              <ActionButton 
                icon={simulationMode ? icons.pause : icons.play}
                text={simulationMode ? 'Desactivar Simulacro' : 'Activar Simulacro'}
                color={simulationMode ? "red" : "green"}
                onClick={() => setSimulationMode(!simulationMode)}
                className="sm:ml-auto"
                darkMode={darkMode}
              />
            </div>
            
            {simulationMode && (
              <div className={`mt-3 p-3 ${darkMode ? 'bg-blue-900' : 'bg-blue-100'} rounded text-sm slide-in`}>
                <p className={`font-medium ${darkMode ? 'text-blue-200' : 'text-blue-800'}`}>
                  Modo simulacro activado: Preguntas aleatorias con temporizador de {simulationTime} minutos.
                </p>
              </div>
            )}
          </div>
          
          <h3 className="text-xl font-bold mb-5">Selecciona un Tema</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {selectedSubject.topics.map((topic) => (
              <TopicCard 
                key={topic.id} 
                topic={topic} 
                onSelect={setSelectedTopic}
                darkMode={darkMode}
              />
            ))}
          </div>
        </div>
      </PageLayout>
    );
  }

  // (g) Con subject y topic seleccionados -> filtrar preguntas y mostrar <Quiz>
  const filteredQuestions = getFilteredQuestions(selectedSubject.id, selectedTopic.id);

  // Verificar si hay material complementario disponible
  const hasComplementaryMaterial = selectedTopic.id === 'ruidosCardiacos' || selectedTopic.id === 'insuficienciaCardiaca';

  return (
    <PageLayout darkMode={darkMode}>
      <div className="space-y-6">
        <div className={`p-8 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-lg'}`}>
          <Quiz
            title={selectedTopic.name}
            questions={filteredQuestions}
            onBack={() => setSelectedTopic(null)}
            onGoHome={handleNavigateHome}
            DecisionTableComponents={selectedTopic.DecisionTableComponents}
            simulationMode={simulationMode}
            subjectName={selectedSubject.name}
            topicName={selectedTopic.name}
            simulationTime={simulationTime}
            darkMode={darkMode}
          />
        </div>
        
        {/* Material complementario en una tarjeta separada */}
        {hasComplementaryMaterial && (
          <div className={`p-8 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-lg'}`}>
            <h3 className="text-xl font-bold mb-5 flex items-center">
              <span className="mr-2 text-blue-500">{icons.externalLink}</span>
              Material Complementario
            </h3>
            
            {selectedTopic.id === 'ruidosCardiacos' && (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-3">
                  <ActionButton 
                    icon={icons.list}
                    text={showRuidosCardiacosTable ? "Ocultar Tabla de Ruidos" : "Mostrar Tabla de Ruidos"}
                    color="green"
                    onClick={() => togglePanel(setShowRuidosCardiacosTable, showRuidosCardiacosTable)}
                    darkMode={darkMode}
                  />
                  
                  <ActionButton 
                    icon={icons.list}
                    text={showEsquemaRuidosCardiacos ? "Ocultar Esquema" : "Mostrar Esquema"}
                    color="blue"
                    onClick={() => togglePanel(setShowEsquemaRuidosCardiacos, showEsquemaRuidosCardiacos)}
                    darkMode={darkMode}
                  />
                  
                  <ActionButton 
                    icon={icons.list}
                    text={showRuidosCardiacosTable2 ? "Ocultar Tabla #2" : "Mostrar Tabla #2"}
                    color="purple"
                    onClick={() => togglePanel(setShowRuidosCardiacosTable2, showRuidosCardiacosTable2)}
                    darkMode={darkMode}
                  />
                </div>
                
                <div className={`collapsible ${showRuidosCardiacosTable ? 'show' : ''}`}>
                  <div className={`p-4 rounded-lg mt-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <h4 className="font-medium mb-3">Tabla de Ruidos Cardíacos</h4>
                    <RuidosCardiacosTable darkMode={darkMode} />
                  </div>
                </div>
                
                <div className={`collapsible ${showEsquemaRuidosCardiacos ? 'show' : ''}`}>
                  <div className={`p-4 rounded-lg mt-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <h4 className="font-medium mb-3">Esquema de Ruidos Cardíacos</h4>
                    <EsquemaRuidosCardiacos darkMode={darkMode} />
                  </div>
                </div>
                
                <div className={`collapsible ${showRuidosCardiacosTable2 ? 'show' : ''}`}>
                  <div className={`p-4 rounded-lg mt-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <h4 className="font-medium mb-3">Tabla de Ruidos Cardíacos #2</h4>
                    <Tabladeruidoscardiacos2 darkMode={darkMode} />
                  </div>
                </div>
              </div>
            )}
            
            {selectedTopic.id === 'insuficienciaCardiaca' && (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-3">
                  <ActionButton 
                    icon={icons.list}
                    text={showHeartFailureTable ? "Ocultar Tabla de Insuficiencia" : "Mostrar Tabla de Insuficiencia"}
                    color="green"
                    onClick={() => togglePanel(setShowHeartFailureTable, showHeartFailureTable)}
                    darkMode={darkMode}
                  />
                  
                  <ActionButton 
                    icon={icons.list}
                    text={showHeartFailureTable1 ? "Ocultar Tabla de Clasificación" : "Mostrar Tabla de Clasificación"}
                    color="purple"
                    onClick={() => togglePanel(setShowHeartFailureTable1, showHeartFailureTable1)}
                    darkMode={darkMode}
                  />
                </div>
                
                <div className={`collapsible ${showHeartFailureTable ? 'show' : ''}`}>
                  <div className={`p-4 rounded-lg mt-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <h4 className="font-medium mb-3">Tabla de Insuficiencia Cardíaca</h4>
                    <HeartFailureTable darkMode={darkMode} />
                  </div>
                </div>
                
                <div className={`collapsible ${showHeartFailureTable1 ? 'show' : ''}`}>
                  <div className={`p-4 rounded-lg mt-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <h4 className="font-medium mb-3">Clasificación de Insuficiencia Cardíaca</h4>
                    <HeartFailureTable1 darkMode={darkMode} />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </PageLayout>
  );
  }; // Cierre de renderMainContent
  
  // Renderizar todo con los modales
  return (
    <>
      {renderMainContent()}
      
      {/* Modal de Login */}
      <LoginModal 
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={(userData) => {
          // Refrescar la suscripción cuando el usuario se loguea
          if (refreshSubscription) {
            refreshSubscription();
          }
          setShowLoginModal(false);
        }}
      />
      
      {/* Modal de Planes y Precios */}
      {showPricingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 p-4 border-b flex justify-between items-center">
              <h2 className="text-2xl font-bold">Planes y Suscripciones</h2>
              <button 
                onClick={() => setShowPricingModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
              >
                ✕
              </button>
            </div>
            <PricingPlans 
              currentPlan={subscription.plan}
              onSelectPlan={(plan) => {
                console.log('Plan seleccionado:', plan);
                setShowPricingModal(false);
              }}
            />
          </div>
        </div>
      )}
      
      {/* Modal de Completar Perfil (para usuarios de Google Auth) */}
      {showCompleteProfile && user && (
        <CompleteProfile 
          user={user}
          onComplete={() => {
            console.log('Perfil completado, cerrando modal');
            // Guardar en localStorage que el perfil está completo
            localStorage.setItem('profile_completed_' + user.id, 'true');
            // Cerrar el modal
            setShowCompleteProfile(false);
            // Marcar como verificado para no volver a verificar
            setProfileChecked(true);
            // Mostrar mensaje de éxito
            toast.success('¡Perfil completado exitosamente!', 5000);
            // Refrescar la suscripción para actualizar los datos
            if (refreshSubscription) {
              refreshSubscription();
            }
            // Mensaje de bienvenida removido para evitar parpadeo
          }}
          onSkip={() => {
            // No permitir saltar - es obligatorio
            toast.warning('Debes completar tu perfil para continuar', 3000);
          }}
        />
      )}
      
      {/* TrialWidget removido — single user con plan pro permanente,
          el FAB flotante era innecesario y estorboso en mobile. */}

      {/* Panel de Admin Dashboard - Nueva versión completa */}
      <AdminDashboard
        isOpen={showAdminDashboard}
        onClose={() => setShowAdminDashboard(false)}
        user={user}
      />

      {/* AdminPanel (FAB escudo púrpura) removido — el botón "Panel Admin"
          ya existe en el Header (md:flex) y abre AdminDashboard. El FAB
          duplicaba función y estorbaba en mobile. */}
      
      {/* Panel Simple de Admin para debugging - SIEMPRE visible en localhost */}
      <AdminPanelSimple />
      
      {/* Libro de Reclamaciones - Obligatorio según INDECOPI */}
      <LibroReclamaciones 
        isOpen={showLibroReclamaciones}
        onClose={() => setShowLibroReclamaciones(false)}
      />
      
    </>
  );
}

export default App;

